import { DraftSaver } from '@shared/services/draft-saver';
import { Subscription } from 'rxjs';
import { HospitalServiceLocationService } from 'app/services/license/hospital/hospital-service-location.service';
import { trigger, transition, style, animate } from "@angular/animations";
import { Component, OnInit, OnDestroy, ViewChildren, QueryList, ViewChild, Input, inject } from "@angular/core";
import { FormGroup, FormControl, Validators } from "@angular/forms";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { SharedService } from "@shared/services/shared.service";
import { AddressComponent } from "app/components/reusable/address/address.component";
import { DocumentComponent } from "app/components/reusable/document/document.component";
import { OfficeHoursComponent } from "app/components/reusable/office-hours/office-hours.component";
import { ISatelliteLocation } from "app/models/licenses/satellite-location/satellite-location";
import { IStatus } from "app/models/licenses/status";
import { AddressService } from "app/services/address/address.service";
import { DocumentService } from "app/services/document/document.service";
import { OfficeHoursService } from "app/services/license/office-hours.service";
import { SatelliteLocationService } from "app/services/satellite-location/satellite-location.service";
import { HospitalServiceLocationComponent } from '../hospital-service-location/hospital-service-location.component';
import { firstValueFrom } from "rxjs";

@Component({
  selector: 'satellite-location',
  templateUrl: './satellite-location.component.html',
  styleUrls: ['./satellite-location.component.scss'],
    animations: [
      trigger('slideRight', [
        transition(':enter', [
          style({ transform: 'translateX(100%)' }),
          animate('200ms ease-in', style({ transform: 'translateX(0%)' }))
        ]),
        transition(':leave', [
          animate('200ms ease-in', style({ transform: 'translateX(100%)' }))
        ])
      ])
    ]
})

export class SatelliteLocationComponent implements OnInit, OnDestroy {
  @ViewChildren(DocumentComponent) documents!: QueryList<DocumentComponent>;
  @ViewChild(HospitalServiceLocationComponent) serviceLocations?: HospitalServiceLocationComponent;
  @ViewChildren(AddressComponent) addresses!: QueryList<AddressComponent>;
  @Input() canEdit: boolean = false
  @Input() entityId: number = 0;
  @Input() entityType: string = '';
  @Input() isChange: boolean = false;
  @Input() licenseStatuses: IStatus[] = [];
  @Input() HospitalLicenseId: string = "";

  public entity: string = 'SatelliteLocation';
  public deletingSatelliteLocation: boolean = false;
  public managingSatelliteLocation: boolean = false;
  public isOutOfState: boolean = false;
  public newSatelliteCreated: boolean = false;
  public includeArchived: boolean = false;
  public satelliteLocationTypes: string[] = ['Map of Location', 'Other'];

  public satelliteLocation: ISatelliteLocation = {
    id: 0,
    satelliteId: "",
    buildingName: "",
    phone: "",
    parentId: 0,
    archived: false,
    addresses: [],
    documents: []
  }

  public satellites: ISatelliteLocation[] = [];
  public satelliteForm = new FormGroup({
    buildingName: new FormControl('', Validators.required),
    phone: new FormControl('', [Validators.required, Validators.pattern(/^\(?([0-9]{3})\)?[-]?([0-9]{3})[-]?([0-9]{4}).*$/)])
  });

  public satelliteLocationColumns: string [] = ['satelliteId', 'buildingName', 'archived'];
  public satelliteLocationSource = new MatTableDataSource<ISatelliteLocation>(this.satellites);
  @ViewChild('satelliteLocationPaginator', {static: false}) satelliteLocationPaginator!: MatPaginator;
  @ViewChild('satelliteLocationSort', {static: false}) satelliteLocationSort!: MatSort;

  public sharedService = inject(SharedService);
  public satelliteLocationService = inject(SatelliteLocationService);
  public addressService = inject(AddressService);
  public officeHoursService = inject(OfficeHoursService);
  public documentService = inject(DocumentService);

  public saver?: DraftSaver;
  public isCreating = false;
  public isClosing = false;
  private creating?: Promise<void>;
  private formChanges?: Subscription;
  private readonly locationService = inject(HospitalServiceLocationService);

  private startSaving(): void {
    this.saver?.dispose();
    this.formChanges?.unsubscribe();
    this.saver = new DraftSaver(async () => {
      const values = this.satelliteForm.getRawValue();
      const record = { ...this.satelliteLocation, buildingName: values.buildingName ?? '', phone: values.phone ?? '' };
      if (!record.id || !this.canEdit) throw new Error('Parent is not editable');
      if (!await firstValueFrom(this.satelliteLocationService.updateSatelliteLocation(record))) throw new Error('Save failed');
      Object.assign(this.satelliteLocation, record);
    }, () => this.sharedService.toastr.error('Could not save satellite location. Your edits are still here.'));
    this.formChanges = this.satelliteForm.valueChanges.subscribe(() => {
      if (this.managingSatelliteLocation && this.canEdit) this.saver?.changed();
    });
  }

  async savePending(): Promise<boolean> {
    await this.creating;
    if (!this.canEdit || !this.managingSatelliteLocation) return true;
    if (this.serviceLocations && !await this.serviceLocations.savePending()) return false;
    return this.saver ? this.saver.flush() : true;
  }

  async closeWithSave(): Promise<void> {
    if (this.isClosing) return;
    this.isClosing = true;
    try {
      if (await this.savePending()) {
        this.saver?.dispose();
        this.formChanges?.unsubscribe();
        this.toggleSatelliteForm(false);
      }
    } finally { this.isClosing = false; }
  }

  async validateServiceLocations(): Promise<string[]> {
    const errors: string[] = [];
    for (const parent of this.satellites.filter(item => !item.archived)) {
      if (!parent.buildingName.trim() || !/^\(?([0-9]{3})\)?[-]?([0-9]{3})[-]?([0-9]{4}).*$/.test(parent.phone)) {
        errors.push('Satellite location has incomplete required details.');
      }
      const locations = await firstValueFrom(this.locationService.getHospitalServiceLocations(parent.id, 'SatelliteLocation', false));
      for (const location of locations) {
        if (location.isDraft) errors.push(
          (parent.buildingName || 'Unnamed satellite') + ': service location ' +
          (location.floor || 'unnamed') + ' / ' + (location.suite || 'unnamed') + ' is incomplete. Complete its details and all days of office hours, or delete the draft.');
      }
    }
    return errors;
  }

  ngOnInit(): void {
    this.getSatelliteLocations(this.includeArchived);
    if(this.licenseStatuses.some(s => s.label === this.sharedService.started))
      this.satelliteLocationColumns = ['buildingName', 'archived'];
    else
      this.satelliteLocationColumns = ['satelliteId', 'buildingName', 'archived'];
  }

  ngOnDestroy(): void {
    this.saver?.dispose();
    this.formChanges?.unsubscribe();
  }

  getSatelliteLocations(includeArchived: boolean): void {
    this.includeArchived = includeArchived;

    this.satelliteLocationService.GetSatelliteLocations(this.entityId, this.entityType, this.includeArchived).subscribe({
      next: response => this.satellites = response,
      complete: () => this.updateSatelliteTable()
    });
  }

  updateSatelliteTable(): void {
    setTimeout(() => {
      this.satelliteLocationSource.data = this.satellites;
      this.satelliteLocationSource.paginator = this.satelliteLocationPaginator;
      this.satelliteLocationSource.sort = this.satelliteLocationSort;
    }, 100);
  }

  selectSatelliteLocation(satelliteLocation: ISatelliteLocation): void {
    this.deletingSatelliteLocation = false;
    this.satelliteLocation = satelliteLocation;
    this.managingSatelliteLocation = true;
    this.toggleSatelliteForm(true);
    this.updateSatelliteForm(satelliteLocation);
    this.startSaving();
  }

  toggleSatelliteForm(adding: boolean): void{
    this.managingSatelliteLocation = adding;
    this.updateSatelliteForm(this.getEmptySatelliteLocation());
    if(!adding){
      this.updateSatelliteTable();
    }
  }

  getEmptySatelliteLocation(): ISatelliteLocation {
    return {
      id: 0,
      satelliteId: "",
      buildingName: "",
      phone: "",
      parentId: this.entityId,
      archived: false,
      addresses: [],
      documents: []
    }
  }

  updateSatelliteForm(satelliteLocation: ISatelliteLocation): void {
    this.satelliteForm.reset({
      buildingName: satelliteLocation.buildingName,
      phone: satelliteLocation.phone
    });
  }

  updateSatelliteLocation(): void {
    let form = this.satelliteForm.getRawValue();
    this.satelliteLocation.buildingName = form.buildingName ?? '';
    this.satelliteLocation.phone = form.phone ?? '';
  }

   async createSatelliteLocation(): Promise<void> {
    if (!this.canEdit || this.isCreating) return;
    this.isCreating = true;
    this.creating = (async () => {
      try {
        const record = { ...this.getEmptySatelliteLocation(), parentId: this.entityId };
        const response = await firstValueFrom(this.satelliteLocationService.createSatelliteLocation(record, this.entityId, this.entityType));
        if (!response.id) throw new Error('Creation failed');
        this.satellites.push(response);
        this.satelliteLocation = response;
        this.toggleSatelliteForm(true);
        this.newSatelliteCreated = true;
        this.deletingSatelliteLocation = false;
        this.startSaving();
      } catch {
        this.sharedService.toastr.error('Could not create satellite location. Please try again.');
      } finally { this.isCreating = false; }
    })();
    await this.creating;
    this.creating = undefined;
   }

   async saveSatelliteLocation(): Promise<void> { await this.closeWithSave(); }

   confirmDelete(satelliteLocationId: number): void {
     this.sharedService.openConfirm('Delete Satellite Location ' + this.satelliteLocation.buildingName + ' ?');
     this.sharedService.confirmed().subscribe(
       confirmed => {
         if(confirmed){
           this.deletingSatelliteLocation = true;
           this.deleteSatelliteLocation(satelliteLocationId);
         }
       }
     );
   }

  async deleteSatelliteLocation(id: number): Promise<void> {
    if (this.isClosing) return;
    this.isClosing = true;
    try {
      await this.saver?.stop();
      await this.serviceLocations?.stopSaving();
      const deleted = await firstValueFrom(this.satelliteLocationService.deleteSatelliteLocation(id));
      if (!deleted) throw new Error('Delete failed');
      this.saver = undefined;
      this.formChanges?.unsubscribe();
      this.newSatelliteCreated = false;
      this.satellites = this.satellites.filter(item => item.id !== id);
      this.toggleSatelliteForm(false);
    } catch {
      this.startSaving();
      this.saver?.changed();
      this.serviceLocations?.resumeSaving();
      this.sharedService.toastr.error('Could not delete location. Your edits are still here.');
    } finally { this.isClosing = false; }
  }

  async validateDocuments(): Promise<string[]> {
     let errors: string[] = [];
     for (const satellite of this.satellites) {
      try {
        const response = await firstValueFrom(
          this.documentService.getDocuments(satellite.id, 'SatelliteLocation', false)
        );
        satellite.documents = response;
        if (satellite.documents.length === 0) {
          errors.push(`Satellite Location ${satellite.buildingName} must upload a map of the location`);
        }
      } catch (e) {
        console.log('Get document error', e);
      }
    }
     return errors;
   }

  updateOutOfState(isOutOfState: boolean): void {
    this.isOutOfState = isOutOfState;
  }

  async validateSatelliteAddresses(): Promise<string[]> {
    let errors: string[] = [];
    for (const satellite of this.satellites) {
      try {
        const response = await firstValueFrom(
          this.addressService.getAddresses(satellite.id, 'SatelliteLocation', false)
        );
        satellite.addresses = response;
        if (!satellite.addresses.find(a =>
          !a.archived && a.addressTypes.find(t => t.type === 'Physical')
        )) {
          errors.push(`Satellite Location ${satellite.buildingName} must have a physical address`);
        }
      } catch (e) {
        console.log('Get address error', e);
      }
    }

    return errors;
  }
}
